import { idsOf, MigrationSpec } from "../migration.models"
import {
  ALL_MARKETPLACE_COLLECTIONS,
  CHAT_ONLY_COLLECTIONS,
  EMAIL_ACCOUNT_COLLECTIONS,
  EMAIL_MARKETPLACE_COLLECTIONS,
  QUESTION_ONLY_MARKETPLACE_COLLECTIONS,
  SOCIAL_PAGE_COLLECTIONS
} from "./channel_collections"

const SERVICE_ROLES = { $in: ["SERVICE", "ECOMMERCE"] }

const hasAny = (path: string) => ({ [`${path}.0`]: { $exists: true } })

const holdsAny = (path: string, collections: ReadonlyArray<string>) => ({
  [path]: { $in: idsOf(collections) }
})

const CHAT_PATHS = [
  "chatSettings.channels",
  "chatSettings.humanHandoff.channels"
]

/**
 * Exactly the profiles qpien-ai's `planChannelScope` writes to. Ids it only reports (eBay in chat,
 * Amazon/Walmart in e-mail, ids in no known collection) are never matched here, so the row reads
 * APPLIED once the runner has nothing left to remove.
 */
const affectedFilter = {
  $or: [
    {
      role: "MARKETPLACE",
      ...holdsAny("marketPlaceSettings.channels", [
        ...SOCIAL_PAGE_COLLECTIONS,
        ...CHAT_ONLY_COLLECTIONS,
        ...EMAIL_ACCOUNT_COLLECTIONS
      ])
    },
    ...[
      ...CHAT_PATHS,
      "emailSettings.channels",
      "commentSettings.channels",
      "ticketSettings.channels"
    ].map((path) => ({ role: "MARKETPLACE", ...hasAny(path) })),
    { role: SERVICE_ROLES, ...hasAny("marketPlaceSettings.channels") },
    ...CHAT_PATHS.map((path) => ({
      role: SERVICE_ROLES,
      ...holdsAny(path, QUESTION_ONLY_MARKETPLACE_COLLECTIONS)
    })),
    // Without its own e-mail block a profile answers e-mail from the chat list, so those stay.
    ...CHAT_PATHS.map((path) => ({
      role: SERVICE_ROLES,
      emailSettings: { $type: "object" },
      ...holdsAny(path, [
        ...EMAIL_ACCOUNT_COLLECTIONS,
        ...EMAIL_MARKETPLACE_COLLECTIONS
      ])
    })),
    ...["emailSettings.channels", "ticketSettings.channels"].map((path) => ({
      role: SERVICE_ROLES,
      ...holdsAny(path, [
        ...SOCIAL_PAGE_COLLECTIONS,
        ...CHAT_ONLY_COLLECTIONS,
        ...QUESTION_ONLY_MARKETPLACE_COLLECTIONS,
        "EbayAccounts"
      ])
    })),
    {
      role: SERVICE_ROLES,
      ...holdsAny("commentSettings.channels", [
        ...CHAT_ONLY_COLLECTIONS,
        ...EMAIL_ACCOUNT_COLLECTIONS,
        ...ALL_MARKETPLACE_COLLECTIONS
      ])
    },
    {
      role: "ECOMMERCE",
      ...holdsAny("storeSettings.integrationStore", [
        "MarketplaceIntegrationStores"
      ])
    }
  ]
}

const aiProfileChannelScope: MigrationSpec = {
  type: "MIGRATE_AI_PROFILE_CHANNEL_SCOPE",
  collection: "AiProfile",
  risk: "DESTRUCTIVE",
  title: {
    en: "AI profiles: drop channels and stores the role cannot use",
    tr: "AI profilleri: rolün kullanamayacağı kanal ve mağazaları çıkar"
  },
  description: {
    en:
      "Removes from every AI profile the channel ids whose platform can never reach that settings " +
      "block (a live chat on the Marketplace role, a Trendyol account in a chat list, an Instagram " +
      "page in e-mail), empties any block the role does not own, and unbinds an E-commerce role from " +
      "a Trendyol/Hepsiburada store. Platforms are looked up by id in the channel collections.",
    tr:
      "Her AI profilinden, platformu o ayar bloğuna hiç mesaj düşüremeyen kanal kimliklerini çıkarır " +
      "(Pazaryeri rolünde live chat, sohbet listesinde Trendyol hesabı, e-postada Instagram sayfası), " +
      "rolün sahibi olmadığı blokları boşaltır ve E-ticaret rolünün Trendyol/Hepsiburada mağaza " +
      "bağlamasını kaldırır. Platform, kimlik kanal koleksiyonlarında aranarak bulunur."
  },
  affectedFilter,
  sampleFields: [
    "customer",
    "role",
    "chatSettings.channels",
    "chatSettings.humanHandoff.channels",
    "emailSettings.channels",
    "commentSettings.channels",
    "ticketSettings.channels",
    "marketPlaceSettings.channels",
    "storeSettings.integrationStore"
  ],
  notes: [
    {
      en:
        "Only REPORTED, never removed — listed in the run result: eBay accounts in a chat list " +
        "(eBay buyer messages are chat), Amazon/Walmart accounts in e-mail or ticket (they arrive as " +
        "e-mail), and ids found in no channel collection. Whether a Service/E-commerce role should " +
        "answer those marketplaces is a separate decision.",
      tr:
        "Yalnızca RAPORLANIR, silinmez — çalıştırma sonucunda listelenir: sohbet listesindeki eBay " +
        "hesapları (eBay alıcı mesajları sohbettir), e-posta/ticket'taki Amazon/Walmart hesapları " +
        "(e-posta olarak gelir) ve hiçbir kanal koleksiyonunda bulunamayan kimlikler. Hizmet/E-ticaret " +
        "rolünün bu pazaryerlerine cevap verip vermeyeceği ayrı bir karardır."
    },
    {
      en:
        "An E-commerce role unbound from a marketplace store answers without a catalogue until the " +
        "tenant picks a storefront in AI > Store.",
      tr:
        "Pazaryeri mağazasından ayrılan E-ticaret rolü, müşteri AI > Mağaza'dan bir mağaza seçene kadar " +
        "katalog olmadan cevap verir."
    },
    {
      en: "Deploy qpien-ai and api-v2 first: the runner and the `$idsOf` channel collections live there.",
      tr: "Önce qpien-ai ve api-v2 deploy edilmelidir: çalıştırıcı ve `$idsOf` kanal koleksiyonları oradadır."
    }
  ]
}

export default aiProfileChannelScope
